import {
  ChangeDetectorRef, ChangeDetectionStrategy, Component, EventEmitter, Input, Output,
  OnInit, OnDestroy, ViewChild
} from '@angular/core';
import { Observable } from 'rxjs';
import * as _ from 'lodash';
import * as moment from 'moment';


import { PanelModalComponent } from 'shared/modal/panel-modal/panel-modal.component';
import { FitmentEditFormComponent } from '../fitment-edit-form/fitment-edit-form.component';
import { segments } from './segments-list';
import { rightPanelNavs } from './right-panel-navs';
import { fitmentDataStub } from './stub';
import { fitmentSegmentData } from './segment-data';
import * as productHelpers from 'shared/product/product-helpers';
import { getAllFiltersMap } from 'shared/search/search-helpers';
import { ActiveFilterModel } from 'shared/search/active-filters/active-filter.model';

import { SegmentValidationService } from 'shared/validation/segment-validation/segment-validation.service';
import { HintsService } from 'shared/hints/hints.service';
import { FitmentsService } from 'shared/fitments/fitments.service';
import { BrandService } from 'shared/brand/brand.service';
import {finalize, takeWhile} from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'fitment-panel',
  templateUrl: 'fitment-panel.component.html',
  styleUrls: ['fitment-panel.component.scss'],
  providers: [SegmentValidationService, HintsService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FitmentPanelComponent implements OnInit, OnDestroy {

  @Input() bulkEdit: boolean = false;
  @Input() selectedIds: Array<any>;
  @Input() selectedRows: Array<any> = [];
  @Input() allSelected: boolean;
  @Input() totalRecords: number;
  @Input() searchResultsColumns: Array<any> = [];
  @Input() selectedProduct: any;
  @Input() searchFilters: Array<ActiveFilterModel>;
  @Input() searchStatus: number|string;

  @Output() onModalHidden: EventEmitter<any> = new EventEmitter<any>();
  @Output() revisionIdChanged: EventEmitter<string|number> = new EventEmitter<string|number>();

  @ViewChild(PanelModalComponent, {static: true})
  panelModal: PanelModalComponent | void;

  @ViewChild(FitmentEditFormComponent, {static: false})
  fitmentEditForm: FitmentEditFormComponent | void;

  segmentsNavigationData: any = {
    segments: segments,
    sideNavSize: 100,
    marginLeft: 38,
    showControls: false,
  };
  rightPanelNavs: any = rightPanelNavs;
  fitmentData: any;
  segmentData: any = fitmentSegmentData;
  sourceData: any = {};
  imageUrl: string;
  uploadedDate: string;
  headerDescription: string;
  isActive: boolean = true;
  positionsLookup: Array<any> = [];
  appIds: Array<any>;
  saving: boolean = false;

  get isModalShown(): boolean {
    return _.get(this, 'panelModal.isModalShown', false);
  }

  get isDirty(): boolean {
    return _.get(this, 'fitmentEditForm.isDirty', false);
  }

  get brandId(): string {
    return this.brandService.getBrand();
  }

  constructor(
    private fitmentsService: FitmentsService,
    private brandService: BrandService,
    private ref: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    if (this.panelModal) {
      this.panelModal.onHidden
        .pipe(takeWhile(() => this.isActive))
        .subscribe(() => {
          this.setData();
          this.positionsLookup = [];
          this.appIds = [];
          this.onModalHidden.emit();
        });
      this.panelModal.onShown
        .pipe(takeWhile(() => this.isActive))
        .subscribe(() => {
          this.ref.markForCheck();
        });
    }
  }

  ngOnDestroy() {
    this.isActive = false;
  }

  show() {
    if (this.panelModal instanceof PanelModalComponent) {
      this.panelModal.show();
    }
    this.fetchPositionLookups();
    this.appIds = this.selectedIds;
    if (this.bulkEdit) {
      this.imageUrl = null;
      this.uploadedDate = null;
      this.headerDescription = null;
      this.setData();
    } else {
      this.setHeaderData();
      this.setData(this.selectedRows[0]);
    }
  }

  // handlers
  onSaveClick() {
    this.saving = true;
    if (!this.fitmentEditForm) {
      return;
    }
    const data = this.prepareDataForSave(this.fitmentEditForm.form.value);
    this.fitmentsService.updateApps(this.brandId, this.selectedProduct._id, data)
    .pipe(finalize(() => {
      this.saving = false;
    }))
    .subscribe((res: any) => {
      this.appIds = _.get(res, 'app_ids', []);
      this.revisionIdChanged.emit(_.get(res, 'item_detail_revision_id'));
      if (this.fitmentEditForm instanceof FitmentEditFormComponent) {
        this.fitmentEditForm.markAsPristine();
      }
      _.defer(() => this.ref.markForCheck());
    });
  }

  onCancelClick() {
    if (this.isDirty) {
      this.setData(this.sourceData);
      if (this.fitmentEditForm instanceof FitmentEditFormComponent) {
        this.fitmentEditForm.markAsPristine();
      }
      _.defer(() => this.ref.markForCheck());
    } else {
      if (this.panelModal instanceof PanelModalComponent) {
        this.panelModal.hide();
      }
    }
  }

  // private
  private setData(data: any = {}) {
    this.fitmentData = data;
    this.sourceData = { ...data };
    this.ref.markForCheck();
  }

  private setHeaderData() {
    this.headerDescription = productHelpers.getHeaderDescription(this.selectedProduct);
    this.uploadedDate = productHelpers.getUpdatedDate(this.selectedProduct);
    this.imageUrl = productHelpers.getProductImageUrl(this.selectedProduct);
  }

  private prepareDataForSave(data: any): any {
    // if user select all results, set search params as filters, status
    if (this.allSelected) {
      data.filters = {};
      if (!_.isNil(this.searchStatus)) {
        _.set(data.filters, 'status', this.searchStatus);
      }
      _.assign(data.filters, getAllFiltersMap(this.searchFilters));
    } else {
      data.app_ids = this.appIds;
    }
    data.mfr_label = _.get(data, 'mfr_label', '').trim();
    data.qty = data.qty || null; // replace empty value '' with null to remove qty
    _.remove(data.notes, ['value', '']);
    if (this.bulkEdit) {
      // remove all empty values in bulk edit
      return _.omitBy(data, (value: any, key: string) => {
        if (_.isArray(value)) { return _.size(value) === 0; }
        return !value;
      });
    }
    return data;
  }

  private fetchPositionLookups() {
    this.fitmentsService.getPositions(
      this.brandId,
      this.selectedProduct._id,
    ).subscribe((data: any) => {
      this.positionsLookup = data;
    });
  }
}
